import { useCallback, useEffect, useState } from 'react';
import {
  createFundingSubmission,
  createSubmissionFromMatch,
  createSubmissionFromSequenceItem,
  deleteFundingSubmission,
  fetchClientFundingSubmissions,
  fetchFundingSubmission,
  fetchFundingSubmissions,
  updateFundingSubmission,
} from '../../services/funding/fundingSubmissionsService';

export function useFundingSubmissions({ agencyId, clientId, filters = {}, autoLoad = true } = {}) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = clientId && agencyId
        ? await fetchClientFundingSubmissions(clientId, agencyId)
        : await fetchFundingSubmissions({
          agencyId,
          clientId,
          status: filters.status,
          lenderType: filters.lenderType,
          productType: filters.productType,
          search: filters.search,
        });
      setSubmissions(result.submissions);
      return result.submissions;
    } catch (err) {
      setError(err?.message || 'Failed to load submissions');
      setSubmissions([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [agencyId, clientId, filters.status, filters.lenderType, filters.productType, filters.search]);

  const create = useCallback(async (payload, targetClientId, targetAgencyId) => {
    const cid = targetClientId || clientId;
    const aid = targetAgencyId || agencyId;
    if (!cid || !aid) return null;

    setSaving(true);
    setError(null);
    try {
      const result = await createFundingSubmission(cid, aid, payload);
      await load();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to create submission');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, clientId, load]);

  const createFromMatch = useCallback(async (matchId, targetAgencyId) => {
    const aid = targetAgencyId || agencyId;
    if (!matchId || !aid) return null;

    setSaving(true);
    setError(null);
    try {
      const result = await createSubmissionFromMatch(matchId, aid);
      await load();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to create submission from match');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, load]);

  const createFromSequenceItem = useCallback(async (sequenceId, itemId, targetAgencyId) => {
    const aid = targetAgencyId || agencyId;
    if (!sequenceId || !itemId || !aid) return null;

    setSaving(true);
    setError(null);
    try {
      const result = await createSubmissionFromSequenceItem(sequenceId, itemId, aid);
      await load();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to create submission from sequence item');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, load]);

  const update = useCallback(async (submissionId, payload, targetAgencyId) => {
    const aid = targetAgencyId || agencyId;
    if (!submissionId || !aid) return null;

    setSaving(true);
    setError(null);
    try {
      const result = await updateFundingSubmission(submissionId, aid, payload);
      await load();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to update submission');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, load]);

  const remove = useCallback(async (submissionId, targetAgencyId) => {
    const aid = targetAgencyId || agencyId;
    if (!submissionId || !aid) return null;

    setSaving(true);
    setError(null);
    try {
      await deleteFundingSubmission(submissionId, aid);
      await load();
      return true;
    } catch (err) {
      setError(err?.message || 'Failed to delete submission');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, load]);

  useEffect(() => {
    if (autoLoad && (agencyId || clientId)) {
      load();
    }
  }, [autoLoad, load, agencyId, clientId]);

  return {
    submissions,
    loading,
    saving,
    error,
    load,
    create,
    createFromMatch,
    createFromSequenceItem,
    update,
    remove,
    setSubmissions,
  };
}

export function useFundingSubmissionDetail({ submissionId, agencyId, autoLoad = true } = {}) {
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!submissionId || !agencyId) {
      setSubmission(null);
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await fetchFundingSubmission(submissionId, agencyId);
      setSubmission(result);
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to load submission');
      setSubmission(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [submissionId, agencyId]);

  useEffect(() => {
    if (autoLoad) load();
  }, [autoLoad, load]);

  return { submission, loading, error, load, setSubmission };
}
